import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { AccountType } from '../../../services/schemas/loanServiceSchemas/accountType';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import {
  getBankAccountRuleForBank,
  normalizeBankAccountInput,
  validateBankAccountNumber
} from '../../../utils/validation/bankAccountNumber';

export type BankAccountForm = {
  bankAccount: {
    accountHolderName: string;
    bankName: string;
    accountNumber: string;
    accountType: string;
    branchCode: string;
  }
}

// List of major South African banks (also offered on the lead form)
export const southAfricanBanks = [
  { name: 'ABSA Bank', code: '632005' },
  { name: 'Capitec Bank', code: '470010' },
  { name: 'First National Bank (FNB)', code: '250655' },
  { name: 'Nedbank', code: '198765' },
  { name: 'Standard Bank', code: '051001' },
  { name: 'African Bank', code: '430000' },
  { name: 'Bidvest Bank', code: '462005' },
  { name: 'Discovery Bank', code: '679000' },
  { name: 'Investec', code: '580105' },
  { name: 'TymeBank', code: '678910' },
  { name: 'Bank Zero', code: '888000' }
];

// Map of bank name abbreviations to full names for dropdown matching
const bankNameMapping: Record<string, string> = {
  'FNB': 'First National Bank (FNB)',
  'ABSA': 'ABSA Bank',
  'CAPITEC': 'Capitec Bank',
  'NEDBANK': 'Nedbank',
  'STANDARD BANK': 'Standard Bank', 
  'STANDARD': 'Standard Bank',
  'AFRICAN BANK': 'African Bank',
  'BIDVEST': 'Bidvest Bank',
  'DISCOVERY': 'Discovery Bank',
  'INVESTEC': 'Investec',
  'TYME': 'TymeBank',
  'BANK ZERO': 'Bank Zero'
};

// Function to normalize bank name
export const normalizeBankName = (bankName: string): string => {
  if (!bankName) return '';
  
  // First, check exact match
  const exactMatch = southAfricanBanks.find(
    bank => bank.name.toLowerCase() === bankName.toLowerCase()
  );
  if (exactMatch) return exactMatch.name;
  
  // Check for abbreviations
  const uppercaseName = bankName.toUpperCase();
  for (const [abbr, fullName] of Object.entries(bankNameMapping)) {
    if (uppercaseName.includes(abbr)) {
      return fullName;
    }
  }
  
  return bankName;
};

export default function BankAccountStep() {
  const { register, formState: { errors }, setValue, watch, getValues } = useFormContext<BankAccountForm>();
  const bankErrors = errors.bankAccount || {};
  const selectedBank = watch('bankAccount.bankName');
  const selectedBankRule = getBankAccountRuleForBank(selectedBank);

  // Normalize bank name on component mount
  useEffect(() => {
    const currentBankName = getValues('bankAccount.bankName');
    if (currentBankName && currentBankName !== 'Other') {
      const normalizedName = normalizeBankName(currentBankName);
      if (normalizedName !== currentBankName) {
        setValue('bankAccount.bankName', normalizedName);
      }
    }
  }, []);

  // Auto-fill branch code when bank is selected
  useEffect(() => {
    if (selectedBank) {
      const bank = southAfricanBanks.find(bank => bank.name === selectedBank);
      if (bank) {
        setValue('bankAccount.branchCode', bank.code);
      }
    }
  }, [selectedBank, setValue]);

  useEffect(() => {
    const currentAccountNumber = normalizeBankAccountInput(getValues('bankAccount.accountNumber'));
    if (currentAccountNumber !== getValues('bankAccount.accountNumber')) {
      setValue('bankAccount.accountNumber', currentAccountNumber);
    }
  }, [selectedBank, getValues, setValue]);

  const accountTypeOptions = [
    {
      value: 'SAVINGS',
      label: 'Savings Account',
    },
    {
      value: 'CURRENT_CHEQUE',
      label: 'Current/Cheque Account',
    },
    {
      value: 'TRANSMISSION',
      label: 'Transmission Account',
    },
    {
      value: 'BUSINESS',
      label: 'Business Account',
    },
  ];

  return (
    <div className="form-section">
      <h6 className="form-section-title">Bank Account Information</h6>
      <p className="text-muted mb-4">
        Please provide your bank account details. This information will be used for loan disbursement and repayment.
      </p>

      <div className="row g-3">
        <div className="col-12">
          <div className="form-group">
            <label htmlFor="accountHolderName">Account Holder Name</label>
            <input
              type="text"
              className={`form-control ${bankErrors.accountHolderName ? 'is-invalid' : ''}`}
              id="accountHolderName"
              placeholder="Enter account holder's full name"
              {...register('bankAccount.accountHolderName', { 
                required: 'Account holder name is required' 
              })}
            />
            {bankErrors.accountHolderName && (
              <div className="invalid-feedback">{bankErrors.accountHolderName.message as string}</div>
            )}
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="form-group">
            <label htmlFor="bankName">Bank Name</label>
            <select
              className={`form-select ${bankErrors.bankName ? 'is-invalid' : ''}`}
              id="bankName"
              {...register('bankAccount.bankName', { 
                required: 'Bank name is required' 
              })}
            >
              <option value="">Select your bank</option>
              {southAfricanBanks.map(bank => (
                <option key={bank.code} value={bank.name}>{bank.name}</option>
              ))}
              <option value="Other">Other</option>
            </select>
            {bankErrors.bankName && (
              <div className="invalid-feedback">{bankErrors.bankName.message as string}</div>
            )}
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="form-group">
            <label htmlFor="branchCode">Branch Code</label>
            <input
              type="text"
              className={`form-control ${bankErrors.branchCode ? 'is-invalid' : ''}`}
              id="branchCode"
              placeholder="Enter branch code"
              {...register('bankAccount.branchCode', { 
                required: 'Branch code is required',
                pattern: {
                  value: /^[0-9]{6}$/,
                  message: 'Please enter a valid 6-digit branch code'
                }
              })}
              readOnly={selectedBank !== undefined && selectedBank !== '' && selectedBank !== 'Other'}
            />
            {bankErrors.branchCode && (
              <div className="invalid-feedback">{bankErrors.branchCode.message as string}</div>
            )}
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="form-group">
            <label htmlFor="accountNumber">Account Number</label>
            <input
              type="text"
              className={`form-control ${bankErrors.accountNumber ? 'is-invalid' : ''}`}
              id="accountNumber"
              placeholder="Enter account number"
              inputMode="numeric"
              maxLength={selectedBankRule?.max ?? 14}
              {...register('bankAccount.accountNumber', { 
                validate: (value) => validateBankAccountNumber(value, getValues('bankAccount.bankName')),
                onChange: (e) => {
                  const raw = e.target.value as string;
                  const trimmed = normalizeBankAccountInput(raw);
                  if (trimmed !== raw) {
                    setValue('bankAccount.accountNumber', trimmed, { shouldValidate: true, shouldDirty: true });
                  }
                },
                onBlur: (e) => {
                  const trimmed = normalizeBankAccountInput(e.target.value as string);
                  setValue('bankAccount.accountNumber', trimmed, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                }
              })}
            />
            {bankErrors.accountNumber && (
              <div className="invalid-feedback">{bankErrors.accountNumber.message as string}</div>
            )}
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="form-group">
            <label htmlFor="accountType">Account Type</label>
            <select
              className={`form-select ${bankErrors.accountType ? 'is-invalid' : ''}`}
              id="accountType"
              {...register('bankAccount.accountType', { 
                required: 'Account type is required' 
              })}
            >
              <option value="">Select account type</option>
              {accountTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {bankErrors.accountType && (
              <div className="invalid-feedback">{bankErrors.accountType.message as string}</div>
            )}
          </div>
        </div>

        <div className="col-12">
          <div className="alert alert-info">
            <div className="d-flex">
              <div className="me-3">
                <i className="fa fa-info-circle fa-lg"></i>
              </div>
              <div>
                <p className="mb-0">
                  <strong>Important:</strong> Please ensure that the account details provided are correct. 
                  Incorrect bank details may result in failed transactions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 
